import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";

// AI form completion (CEO, 30/10/2026; FORM_COMPLETION_ENABLED on the API,
// VITE_FORM_COMPLETION_ENABLED on the web). A form template is a blank PDF
// form with the field list the clinician confirmed once; it never holds
// patient data. A completed form belongs to a visit: ROSC fills its clinical
// fields from the visit, the clinician reviews them and types the patient's
// identifiers, and the original PDF is returned with the values written in.
// Completed forms are stored encrypted and deleted with the visit's
// retention.

export const FORM_PDF_MAX_BYTES = 10485760;
export const FORM_PAGES_MAX = 30;
export const FORM_TEMPLATE_MAX = 100;
export const FORM_TEMPLATE_NAME_MAX = 120;
export const FORM_FIELDS_MAX = 400;
export const FORM_FIELD_LABEL_MAX = 200;
export const FORM_FIELD_HINT_MAX = 300;
export const FORM_FIELD_OPTIONS_MAX = 30;
export const FORM_VALUE_MAX = 4000;
export const FORM_SOURCES_MAX = 10;

export const FORM_FIELD_TYPES = [
  "text",
  "multiline",
  "date",
  "checkbox",
  "radio",
  "select",
] as const;
export const formFieldTypeSchema = z.enum(FORM_FIELD_TYPES);
export type FormFieldType = z.output<typeof formFieldTypeSchema>;

// clinical: ROSC may fill it from the visit. identifier: the patient's
// name, date of birth, health card number and the like; never sent to the
// model, typed by the clinician. signature: signatures and attestations;
// always left blank.
export const FORM_FIELD_ROLES = [
  "clinical",
  "identifier",
  "signature",
] as const;
export const formFieldRoleSchema = z.enum(FORM_FIELD_ROLES);
export type FormFieldRole = z.output<typeof formFieldRoleSchema>;

// Fractions of the page (0–1), from its top-left corner.
const fraction = z.number().min(0).max(1);
export const formFieldBoxSchema = z.strictObject({
  x: fraction,
  y: fraction,
  width: fraction,
  height: fraction,
});
export type FormFieldBox = z.output<typeof formFieldBoxSchema>;

const chars = (max: number) => (value: string) =>
  Array.from(value).length <= max;
const line = (max: number) =>
  z
    .string()
    .transform((value) => value.normalize("NFC").replace(/\s+/g, " ").trim())
    .pipe(z.string().min(1).refine(chars(max), `At most ${max} characters`));

export const formFieldSchema = z
  .strictObject({
    // Stable within the template (not a database id).
    key: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[A-Za-z0-9_-]+$/),
    label: line(FORM_FIELD_LABEL_MAX),
    type: formFieldTypeSchema,
    options: z
      .array(line(FORM_FIELD_LABEL_MAX))
      .max(FORM_FIELD_OPTIONS_MAX)
      .default([]),
    role: formFieldRoleSchema,
    page: z.int().min(1).max(FORM_PAGES_MAX),
    // Flat forms: where the value is written. Null for AcroForm fields.
    box: formFieldBoxSchema.nullable().default(null),
    // AcroForm forms: the PDF field name the value goes into.
    acroName: z.string().min(1).max(500).nullable().default(null),
    // A short instruction for filling (e.g. "Dates off work, from–to").
    hint: z
      .string()
      .transform((value) => value.replace(/\s+/g, " ").trim())
      .pipe(z.string().refine(chars(FORM_FIELD_HINT_MAX)))
      .default(""),
  })
  .refine(
    (field) =>
      field.type === "radio" || field.type === "select"
        ? field.options.length > 0
        : true,
    "Choice fields need options",
  );
export type FormField = z.output<typeof formFieldSchema>;
export type FormFieldInput = z.input<typeof formFieldSchema>;

const uniqueKeys = (fields: { key: string }[]) =>
  new Set(fields.map((field) => field.key)).size === fields.length;
export const formFieldsSchema = z
  .array(formFieldSchema)
  .max(FORM_FIELDS_MAX)
  .refine(uniqueKeys, "Field keys must be unique");

export const FORM_KINDS = ["acroform", "flat"] as const;
export const FORM_TEMPLATE_STATES = ["draft", "ready"] as const;
export const formTemplateSchema = z.object({
  public_id: publicIdSchema,
  name: z.string().min(1),
  kind: z.enum(FORM_KINDS),
  source: z.enum(["upload", "starter"]),
  // Draft: fields detected, not yet confirmed by the clinician.
  state: z.enum(FORM_TEMPLATE_STATES),
  pageCount: z.int().min(1),
  fields: z.array(formFieldSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type FormTemplate = z.output<typeof formTemplateSchema>;

export const FORM_STARTER_KEYS = ["work-absence-note"] as const;
export const formStarterKeySchema = z.enum(FORM_STARTER_KEYS);
export type FormStarterKey = z.output<typeof formStarterKeySchema>;
export const formStarterSchema = z.object({
  key: formStarterKeySchema,
  name: z.string(),
});

export const formTemplateListSchema = z.object({
  items: z.array(formTemplateSchema).max(FORM_TEMPLATE_MAX),
  starters: z.array(formStarterSchema),
});

const base64 = (maxBytes: number) =>
  z
    .string()
    .min(4)
    .max(Math.ceil(maxBytes / 3) * 4)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, "Expected base64");

export const formTemplateCreateSchema = z.union([
  z.strictObject({
    // A blank PDF form, base64.
    pdf: base64(FORM_PDF_MAX_BYTES),
    name: line(FORM_TEMPLATE_NAME_MAX).optional(),
  }),
  z.strictObject({ starterKey: formStarterKeySchema }),
]);
export type FormTemplateCreateInput = z.input<typeof formTemplateCreateSchema>;

export const formTemplateUpdateSchema = z
  .strictObject({
    name: line(FORM_TEMPLATE_NAME_MAX).optional(),
    fields: formFieldsSchema.optional(),
    // The clinician confirmed the field list: the template can be used.
    ready: z.literal(true).optional(),
  })
  .refine(
    (value) => Object.values(value).some((field) => field !== undefined),
    "An edit is required",
  );
export type FormTemplateUpdateInput = z.input<typeof formTemplateUpdateSchema>;

// Why an upload was refused (the error message, so clients can explain it).
export const FORM_REFUSALS = {
  notPdf: "This file is not a PDF",
  xfa: "XFA forms are not supported; upload a flattened or fillable version",
  encrypted:
    "Encrypted PDFs are not supported; upload a flattened or fillable version",
  tooManyPages: "This form has too many pages",
  prefilled: "This form already has answers; upload a blank copy",
  noFields: "No fields were found in this form",
  unsupportedCharacters:
    "Some values use characters this form cannot print yet",
} as const;

// One field's value in a completed form.
// filled: from the visit, with its source sentence. uncertain: the clinician
// must check it (low confidence, or its sentence was not found). blank: no
// evidence. identifier / signature: never filled by ROSC.
export const FORM_ENTRY_STATUSES = [
  "filled",
  "uncertain",
  "blank",
  "identifier",
  "signature",
] as const;
export const formEntryStatusSchema = z.enum(FORM_ENTRY_STATUSES);
export type FormEntryStatus = z.output<typeof formEntryStatusSchema>;
export const formSourceRefSchema = z.enum(["note", "transcript", "source"]);
export const formEntrySchema = z.object({
  fieldKey: z.string(),
  value: z.string().nullable(),
  status: formEntryStatusSchema,
  confidence: z.number().min(0).max(1).nullable(),
  sourceQuote: z.string().nullable(),
  sourceRef: formSourceRefSchema.nullable(),
  sourceId: z.int().positive().nullable(),
  // The clinician accepted, edited or cleared the value.
  reviewed: z.boolean(),
});
export type FormEntry = z.output<typeof formEntrySchema>;

export const COMPLETED_FORM_STATES = ["review", "completed"] as const;
export const completedFormSummarySchema = z.object({
  public_id: publicIdSchema,
  templatePublicId: publicIdSchema.nullable(),
  name: z.string(),
  state: z.enum(COMPLETED_FORM_STATES),
  createdAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
});
export type CompletedFormSummary = z.output<typeof completedFormSummarySchema>;
export const completedFormSchema = completedFormSummarySchema.extend({
  kind: z.enum(FORM_KINDS),
  fields: z.array(formFieldSchema),
  entries: z.array(formEntrySchema),
  // Identifier field values the clinician typed (field key → value).
  identifiers: z.record(z.string(), z.string()),
});
export type CompletedForm = z.output<typeof completedFormSchema>;

export const completedFormCreateSchema = z.strictObject({
  templatePublicId: publicIdSchema,
  // Visit sources (ids from listVisitSources) the form may use.
  sourceIds: z.array(z.int().positive()).max(FORM_SOURCES_MAX).default([]),
});
export type CompletedFormCreateInput = z.input<
  typeof completedFormCreateSchema
>;

const valueSchema = z
  .string()
  .transform((value) => value.normalize("NFC").replace(/\r\n?/g, "\n").trim())
  .pipe(z.string().refine(chars(FORM_VALUE_MAX)));
export const completedFormUpdateSchema = z.strictObject({
  // Field key → value (null clears it). Only clinical fields.
  values: z.record(z.string(), valueSchema.nullable()).default({}),
  // Field keys the clinician accepted as they are.
  accepted: z.array(z.string()).max(FORM_FIELDS_MAX).default([]),
  // Identifier field key → value.
  identifiers: z.record(z.string(), valueSchema).default({}),
  // Write the PDF. Refused while uncertain values are not reviewed.
  complete: z.boolean().default(false),
});
export type CompletedFormUpdateInput = z.input<
  typeof completedFormUpdateSchema
>;

export const completedFormPdfSchema = z.object({
  filename: z.string(),
  mediaType: z.literal("application/pdf"),
  data: z.string(),
});

const errors = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  NOT_FOUND: { message: "Form not found" },
  CONFLICT: { message: "Form conflict" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  SERVICE_UNAVAILABLE: { message: "Form completion unavailable" },
});
const query = z.strictObject({}).default({});
const params = z.strictObject({ public_id: publicIdSchema });
const meta = (
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  successStatus = 200,
) =>
  openapi({
    method,
    path: `/api/v2${path}`,
    inputStructure: "detailed",
    successStatus,
    spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
  });
const noBody = z.undefined().optional();

export const formCompletionContract = {
  listFormTemplates: errors
    .input(z.object({ query, body: noBody }))
    .meta(meta("GET", "/form-templates"))
    .output(formTemplateListSchema),
  createFormTemplate: errors
    .input(z.object({ query, body: formTemplateCreateSchema }))
    .meta(meta("POST", "/form-templates", 201))
    .output(formTemplateSchema),
  updateFormTemplate: errors
    .input(z.object({ params, query, body: formTemplateUpdateSchema }))
    .meta(meta("PATCH", "/form-templates/{public_id}"))
    .output(formTemplateSchema),
  deleteFormTemplate: errors
    .input(z.object({ params, query, body: noBody }))
    .meta(meta("DELETE", "/form-templates/{public_id}"))
    .output(z.object({ public_id: publicIdSchema })),
  listVisitForms: errors
    .input(z.object({ params, query, body: noBody }))
    .meta(meta("GET", "/visits/{public_id}/forms"))
    .output(z.object({ items: z.array(completedFormSummarySchema) })),
  createVisitForm: errors
    .input(z.object({ params, query, body: completedFormCreateSchema }))
    .meta(meta("POST", "/visits/{public_id}/forms", 201))
    .output(completedFormSchema),
  getVisitForm: errors
    .input(z.object({ params, query, body: noBody }))
    .meta(meta("GET", "/visit-forms/{public_id}"))
    .output(completedFormSchema),
  updateVisitForm: errors
    .input(z.object({ params, query, body: completedFormUpdateSchema }))
    .meta(meta("PATCH", "/visit-forms/{public_id}"))
    .output(completedFormSchema),
  getVisitFormPdf: errors
    .input(z.object({ params, query, body: noBody }))
    .meta(meta("GET", "/visit-forms/{public_id}/pdf"))
    .output(completedFormPdfSchema),
  deleteVisitForm: errors
    .input(z.object({ params, query, body: noBody }))
    .meta(meta("DELETE", "/visit-forms/{public_id}"))
    .output(z.object({ public_id: publicIdSchema })),
};

// Values the clinician still has to check before the PDF can be written.
export const pendingFormReview = (entries: readonly FormEntry[]): FormEntry[] =>
  entries.filter((entry) => entry.status === "uncertain" && !entry.reviewed);
